import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  subscribeNotifications,
  markAllNotificationsRead,
  migrateLocalStorageNotifications,
  countAccountLevelUnread,
  notificationThreadLink,
  notificationSigninLink,
  notificationFixVerb,
  FirestoreNotification,
  BELL_ROWS,
} from '../repositories/notificationsRepository';
import { openThreadFromNotification } from '../lib/threadDeepLink';
import { followNoticeLink } from '../lib/noticeLink';
import { useNotificationThreadClosure } from '../hooks/useNotificationThreadClosure';
import { resolveThreadClosure } from '../services/groupThreads/threadClosure';
import ClosedThreadNotice from './common/ClosedThreadNotice';

// Reusable notifications bell/dropdown for use on any page header.
// Usage: Place <NotificationsBell /> near the profile avatar. It renders only
// when the user is authenticated and has an accountId.

const NotificationIconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  margin: 0;
  position: relative;

  &:hover {
    background: #fff;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  }

  svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const NotificationWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const BadgeCounter = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: var(--brand-danger);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #fff, 0 6px 12px rgba(226, 74, 74, 0.35);
  pointer-events: none;
`;

const NotificationDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 44px;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  width: 320px;
  padding: 8px;
  z-index: 15000;
  transform-origin: top right;
  transform: ${props => props.$isOpen ? 'scale(1)' : 'scale(0.98)'};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.18s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 1px solid rgba(0,0,0,0.06);
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px 10px;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  margin: 0 2px 6px;

  h4 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: #333;
    text-transform: lowercase;
  }
`;

const NotificationList = styled.div`
  max-height: 360px;
  overflow-y: auto;
  padding: 4px 2px;
`;

const NotificationItem = styled.div<{ $unread?: boolean; $clickable?: boolean; $retired?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  border-radius: 10px;
  transition: background 0.2s ease, color 0.16s ease;
  background: ${props => props.$retired
    ? 'rgba(0,0,0,0.025)'
    : props.$unread ? 'rgba(74, 144, 226, 0.04)' : 'transparent'};
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:hover {
    background: rgba(0,0,0,0.04);
  }

  /* RETIRED (2026-09-21): a thread that is closed to this person.
     THE GREY IS A PALETTE SHIFT, NOT opacity — and that is measured, not a
     preference. The mentions tab dims its rows with opacity precisely because
     opacity has no palette surface; here it cannot be used at all. These rows
     sit on WHITE, and the timestamp is --brand-text-muted (#6E695F), which
     theme.ts records as 4.52:1 — already AT the floor. opacity composites a
     child toward the background and cannot be undone by a descendant, so ANY
     dim takes the meta line under 4.5:1 (0.9 measures 4.44:1). So the text
     goes greyer by COLOUR, every value still clearing 4.5:1 on white, and
     opacity is used only on the icon, which is decorative. Hover restores it,
     because the summary must stay readable and readable is the point. */
  ${props => props.$retired && `
    .icon { opacity: 0.45; }
    .title { color: var(--brand-text-secondary); }
    .message { color: #6a6a6a; }
  `}

  &:hover, &:focus-within {
    .icon { opacity: 1; }
    .title { color: #222; }
    .message { color: #555; }
  }

  &:focus-visible {
    outline: 2px solid var(--brand-gold-hover);
    outline-offset: -2px;
  }

  .icon {
    width: 20px;
    height: 20px;
    color: #4A90E2;
    flex: 0 0 auto;
    margin-top: 2px;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .title {
    font-weight: 600;
    color: #222;
    font-size: 0.92rem;
    line-height: 1.2;
  }

  .message {
    color: #555;
    font-size: 0.88rem;
    line-height: 1.25;
    word-break: break-word;
  }

  .meta {
    color: var(--brand-text-muted);
    font-size: 0.78rem;
  }
`;

const NotificationEmpty = styled.div`
  padding: 16px 12px;
  text-align: center;
  color: #666;
  font-size: 0.92rem;
`;

const ViewAllButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  margin-top: 6px;
  border: none;
  background: rgba(0,0,0,0.04);
  color: #333;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  text-transform: lowercase;

  &:hover {
    background: rgba(0,0,0,0.06);
  }
`;

export const NotificationsBell: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const hasAccount = isAuthenticated && !!(user && user.accountId);
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<FirestoreNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // One-time migration from localStorage to Firestore
  useEffect(() => {
    if (!hasAccount || !user?.id || !user?.accountId) return;
    migrateLocalStorageNotifications(user.id, user.accountId).catch(() => {});
  }, [hasAccount, user?.id, user?.accountId]);

  // Subscribe to real-time Firestore notifications
  useEffect(() => {
    if (!hasAccount || !user?.id) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    const unsubscribe = subscribeNotifications(
      user.id,
      (notifs) => setNotifications(notifs),
      () => setNotifications([]),
    );
    return unsubscribe;
  }, [hasAccount, user?.id]);

  // Account-level unread count (2026-09-08): rows that belong to ONE thread
  // (routine runs, thread messages — anything with a thread_id) are counted
  // by that thread's own badge in the chat, not here.
  useEffect(() => {
    if (!hasAccount) return;
    setUnreadCount(countAccountLevelUnread(notifications));
  }, [hasAccount, notifications]);

  // Mark all as read when opening dropdown
  useEffect(() => {
    if (!isOpen || !hasAccount || !user?.id) return;
    if (unreadCount > 0) {
      markAllNotificationsRead(user.id).catch(() => {});
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!isOpen) return;
      if (wrapperRef.current && event.target instanceof Element) {
        if (!wrapperRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const formatDateTime = (iso: string) => {
    try {
      const d = new Date(iso);
      return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } catch {
      return iso;
    }
  };

  // A closed thread is closed here too (2026-09-21 owner directive).
  //
  // `isOpen` IS AN INPUT, not an optimisation: this dropdown is hidden with
  // opacity/visibility, never unmounted, so its rows render on every page that
  // carries the header. Without it, every signed-in visitor to /pricing would
  // spend a doc read per thread-scoped row for a panel they never opened.
  const rows = useMemo(() => notifications.slice(0, BELL_ROWS), [notifications]);
  const rowThreadIds = useMemo(
    () => rows.map((n) => notificationThreadLink(n)?.threadId).filter((id): id is string => Boolean(id)),
    [rows],
  );
  const closedThreads = useNotificationThreadClosure(user?.id ?? null, rowThreadIds, isOpen);

  if (!hasAccount) return null;

  return (
    <NotificationWrapper ref={wrapperRef}>
      <NotificationIconButton onClick={() => setIsOpen(v => !v)} aria-label="notifications" title="notifications">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        {unreadCount > 0 && (
          <BadgeCounter data-bell-unread-count={unreadCount}>{unreadCount > 99 ? '99+' : unreadCount}</BadgeCounter>
        )}
      </NotificationIconButton>
      <NotificationDropdown $isOpen={isOpen} onClick={e => e.stopPropagation()}>
        <NotificationHeader>
          <h4>notifications</h4>
          <button
            onClick={() => { setIsOpen(false); navigate('/notifications'); }}
            aria-label="view all notifications"
            title="view all"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6 }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </NotificationHeader>
        <NotificationList>
          {rows.length === 0 ? (
            <NotificationEmpty>no notifications yet</NotificationEmpty>
          ) : (
            rows.map(n => {
              const link = notificationThreadLink(n);
              // The thread is closed to this person: the row stays (the bell is
              // a record of what happened) and loses every affordance. Not
              // `aria-disabled`: this row is a div wearing role="button", and a
              // disabled fake control is announced by nothing and reachable by
              // nobody — the real, focusable control is the pill below, which
              // is also what carries the explanation.
              const closure = link
                ? resolveThreadClosure(closedThreads.get(link.threadId), user?.id ?? null)
                : null;
              // Sign in once, S5: the notice that a sign-in ended opens that sign-in's window (the Integrations page).
              const signinPath = link ? null : notificationSigninLink(n);
              const openable = (Boolean(link) && !closure) || Boolean(signinPath);
              // A thread-scoped row opens its thread (2026-09-08): same
              // workspace → the chat opens on it; another workspace → the
              // sealed switch — never an in-place foreign open.
              const open = openable && link
                ? () => {
                    setIsOpen(false);
                    openThreadFromNotification(link);
                  }
                : signinPath
                  ? () => {
                      setIsOpen(false);
                      followNoticeLink(signinPath, navigate);
                    }
                  : undefined;
              return (
              <NotificationItem
                key={n.id}
                $unread={!n.read}
                $clickable={openable}
                $retired={Boolean(closure)}
                data-notification-thread-id={link ? link.threadId : undefined}
                {...(closure ? { 'data-notification-retired': 'true' } : {})}
                role={openable ? 'button' : undefined}
                tabIndex={openable ? 0 : undefined}
                aria-label={openable ? (signinPath ? `${notificationFixVerb(signinPath)}: ${n.title || 'notification'}` : `open thread for: ${n.title || 'notification'}`) : undefined}
                onClick={open}
                onKeyDown={openable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open?.(); } } : undefined}
              >
                <span className="icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
                    <path d="M13.73 21a2 2 0 01-3.46 0"/>
                  </svg>
                </span>
                <div className="content">
                  <div className="title">{n.title || 'notification'}</div>
                  <div className="message">{n.message}</div>
                  <div className="meta">
                    {formatDateTime(n.timestamp)}
                    {(n.metadata?.type === 'session_ended' || n.metadata?.type === 'chat_session') && n.metadata?.report_id && (
                      <span
                        style={{ marginLeft: 8, color: '#4A90E2', cursor: 'pointer', fontWeight: 600 }}
                        onClick={(e) => { e.stopPropagation(); setIsOpen(false); navigate('/notifications'); }}
                      >
                        view transcript
                      </span>
                    )}
                    {link && !closure && (
                      <span style={{ marginLeft: 8, color: 'var(--brand-gold-text)', fontWeight: 600 }} data-notification-open-thread="">
                        open thread
                      </span>
                    )}
                    {signinPath && (
                      <span style={{ marginLeft: 8, color: 'var(--brand-gold-text)', fontWeight: 600 }} data-notification-sign-in-again="">
                        {notificationFixVerb(signinPath)}
                      </span>
                    )}
                  </div>
                  {closure && (
                    <ClosedThreadNotice
                      closure={closure}
                      description={n.title || 'notification'}
                      rowId={n.id}
                    />
                  )}
                </div>
              </NotificationItem>
              );
            })
          )}
        </NotificationList>
        <ViewAllButton onClick={() => { setIsOpen(false); navigate('/notifications'); }}>view all</ViewAllButton>
      </NotificationDropdown>
    </NotificationWrapper>
  );
};

export default NotificationsBell;
